import { HotspotSidepanelData } from './HotspotSimple';
import { SidepanelMediaDataJSON } from './HotspotSidepanelItem';
import GenericImageGenerator from '@owc-utils/GenericImage/GenericImageGenerator';
import { Options as PlayerSettings } from 'plyr';
import initializeTracking, { formatTrackingString } from '@owc-packages/Tracking/src/TrackingUtils';
import { addFootnotesToStore } from '@owc-utils/Footnotes/FootnotesHelper';
import { HotspotSidepanel } from './Hotspot';

const SIDEPANEL_GRID_COLUMNS_WIDTH = 6;
const PIXEL_PER_REM = 16;
const GRID_COLUMN_AMOUNT = 12;
const GRID_MAX_PIXEL_WIDTH = '1680px';
const GRID_MAX_PERCENTAGE_WIDTH = '86%';

const enum imageCropSets {
  fourByThree = '4x3',
  sixteenByNine = '16x9',
}

export function getHotspotSidepanelWidth(containerWidth: number): number {
  const wbGridGutterWidth = getComputedStyle(document.documentElement).getPropertyValue(
    '--wb-grid-gutter-width',
  );
  let wbGridContentWidth = getComputedStyle(document.documentElement).getPropertyValue(
    '--wb-grid-content-width',
  );

  if (wbGridContentWidth === GRID_MAX_PIXEL_WIDTH) {
    wbGridContentWidth = GRID_MAX_PERCENTAGE_WIDTH;
  }

  const gutterWidth = Number(wbGridGutterWidth.replace('rem', '')) * PIXEL_PER_REM;
  const contentWidth = Number(wbGridContentWidth.replace('%', '')) / 100;

  const ratio = SIDEPANEL_GRID_COLUMNS_WIDTH / GRID_COLUMN_AMOUNT;

  return containerWidth * contentWidth * ratio - gutterWidth * (1 - ratio);
}

export function calculateWalkDistance(
  imgContainer: Element,
  parent: Element,
  coordinateX: string,
  isRtl: boolean,
): number {
  const imageWidth = imgContainer.clientWidth;
  const imageScrollWidth = imgContainer.scrollWidth;
  const imageScrollOffset = imgContainer.scrollLeft;
  // If there is a hotspot on the the left edge of the image,
  // it makes no sense to position the hotspot on horizontal center
  // because the gap between the sidepanel and the image is visible.
  // that's why move the image to the the edge of the sidepanel.
  const sidepanelWidth = getHotspotSidepanelWidth(parent.clientWidth);
  // calculate pixel values out of percentage values.
  // hotspot position is configured in percentage
  const parsedCoordinateX = parseFloat(coordinateX) / 100;
  const coordinateXinPixel = imageScrollWidth * parsedCoordinateX;
  const maxWalkableDistance = (imageWidth - sidepanelWidth) * 0.5;

  // `walkDistance` is the distance the hotspot has to move.
  // the movement is only appied if the hotspot lies right half
  // of the image.
  // [ | ] < image
  //   ^ center
  // hotspots on the left side of the center are untouched.
  // hotspots on the right side of the center should move the
  // horizontal center.
  // this is inverted for rtl context since the sidepanel opens
  // from the left instead.
  let walkDistance = isRtl
    ? maxWalkableDistance - (imageWidth - coordinateXinPixel) + imageScrollOffset
    : maxWalkableDistance - coordinateXinPixel + imageScrollOffset;
  // the result of the `waltDistance` is negative because you move
  // on the x-axis to the center (move to the left).
  if (walkDistance > 0 && maxWalkableDistance - walkDistance > 0) {
    walkDistance = 0;
  }
  // if the `walkDistance` is greater then the with of the sidepanel
  // then stop on the edge of the sidepanel
  if (walkDistance + sidepanelWidth < 0) {
    walkDistance = sidepanelWidth * -1;
  }

  return Math.floor(walkDistance);
}

const videoPlayerSettings: PlayerSettings = {
  autopause: true,
  muted: true,
  resetOnEnd: true,
  loop: {
    active: true,
  },
};

function evaluateCropSet(mqValue: string | undefined) {
  return mqValue === imageCropSets.sixteenByNine;
}

export function transformToUnifiedMediaFormat(mediaData: SidepanelMediaDataJSON | undefined) {
  let mediaObject;
  if (mediaData) {
    const ratio = mediaData.ratio;
    const mqValue = evaluateCropSet(ratio);
    if (mediaData.video) {
      const type = mediaData.type;
      mediaObject = {
        ...mediaData.video,
        thumbnail: {
          ...mediaData.image,
          ratio: ratio,
        },
        videoDetails: {
          openInModal: false,
          type: type,
        },
        videoPlayerSettings,
      };
    } else {
      const genericImage = new GenericImageGenerator(mediaData.image, [], ratio, mqValue);
      mediaObject = {
        ...genericImage,
      };
    }
  }
  return mediaObject;
}
export const customItemTrackingTrigger = () => {
  let triggered = false;
  return async (el: Element, hotspot: HotspotSidepanelData) => {
    if (!triggered) {
      const trackingInstance = await initializeTracking();
      triggered = true;
      trackingInstance.customTrackingObjectUpdate(
        {
          label: `spot.${formatTrackingString(hotspot.hotspotText)}`,
          category: 'feature',
        },
        'click',
        el,
      );
    }
  };
};

export const formatPropToAttribute = (prop: string): string =>
  prop
    .trim()
    .replace(/[A-Z]/g, '-$&')
    .toLowerCase();

export const calculateSimpleItemTooltipPosition = (
  item?: {
    coordinateX: string;
    coordinateY: string;
  },
  isRtl?: boolean,
): string => {
  let position = 'top';
  if (item) {
    const xCoordinate = parseFloat(item.coordinateX);
    const yCoordinate = parseFloat(item.coordinateY);
    const minValueToOpenTooltipOnTop = 7.5;
    const fullSize = 100; // 100% of the image
    // hotspot placed on very top edge of the image -> tooltip opens to the bottom
    if (yCoordinate < minValueToOpenTooltipOnTop) {
      position = 'bottom';
    }
    // hotspot placed on very left edge of the image -> tooltip opens to the right
    if (xCoordinate < minValueToOpenTooltipOnTop) {
      position = isRtl ? 'start' : 'end';
    }
    // hotspot placed on very right edge of the image -> tooltip opens to the left
    if (xCoordinate > fullSize - minValueToOpenTooltipOnTop) {
      position = isRtl ? 'end' : 'start';
    }
  }
  return position;
};

export const setFootnotes = (hotspotData: HotspotSidepanel[]): void => {
  hotspotData
    .filter((hotspot: HotspotSidepanel) => hotspot.sidepanel.footnotes)
    .forEach((hotspot: HotspotSidepanel) => {
      if (hotspot.sidepanel.footnotes) {
        addFootnotesToStore(hotspot.sidepanel.footnotes);
      }
    });
};
